import AddIcon from '@mui/icons-material/Add';
import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import TextField from '@mui/material/TextField';
import React, { useCallback, useMemo, useState } from 'react';

import AsyncLoader from '~components/AsyncLoader';
import { DotLoader } from '~components/DotLoader';
import EmptyState from '~components/EmptyState';
import QueueCard from '~components/QueueCard';
import useDebounce from '~hooks/useDebounce';
import { useNotification } from '~providers/NotificationProvider';
import { APIError, UnsupportedStructureError } from '~services/Errors';

import { createSipQueue, deleteSipQueue, updateSipQueue } from '../../SipConfiguration/api';
import { NewSipQueue, SipQueue } from '../../SipConfiguration/domain';
import useSipQueues from '../useSipQueues';
import QueueModal from './QueueModal';

const QueuesList = () => {
  const { pushNotification } = useNotification();
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editingQueue, setEditingQueue] = useState<SipQueue | null>(null);
  const [submittingData, setSubmittingData] = useState<boolean>(false);
  const [search, setQuery] = useState<string>('');
  const debouncedSearch = useDebounce(search, 500);
  const {
    list,
    loading,
    hasMore,
    error,
    reload,
    intersectionObserverRef: lastDataElement,
  } = useSipQueues({
    search: debouncedSearch,
  });

  const closeModal = () => {
    setCreateModalOpen(false);
    setEditingQueue(null);
  };

  const updateQueue = useCallback(
    async (data: NewSipQueue) => {
      setSubmittingData(true);
      try {
        if (editingQueue === null) {
          await createSipQueue(data);
        } else {
          await updateSipQueue({ queueID: editingQueue.queueID, ...data });
        }
        pushNotification('success', `Created SIP queue ${data.queueName}`);
        await reload();
        closeModal();
      } catch (e) {
        pushNotification('error', (e as APIError | UnsupportedStructureError).message);
      } finally {
        setSubmittingData(false);
      }
    },
    [editingQueue],
  );

  const deleteQueue = async (queueID: number) => {
    try {
      await deleteSipQueue(queueID);
      await reload();
      pushNotification('success', `Deleted queue ${queueID}`);
    } catch (e) {
      pushNotification('error', (e as APIError | UnsupportedStructureError).message);
    }
  };

  const isListEmpty = list.length === 0;
  const noResult = !loading && isListEmpty;
  const displayList = useMemo(
    () =>
      list.map((item, index) => (
        <QueueCard
          title={
            <>
              <Typography variant='body2'>ID: {item.queueID}</Typography>
              {item.queueName}
            </>
          }
          subTitle={item.description}
          key={item.queueID}
          onEdit={() => setEditingQueue(item)}
          onDelete={() => deleteQueue(item.queueID)}
          name={item.queueName}
          ref={index === list.length - 1 ? lastDataElement : null}
        />
      )),
    [list, lastDataElement],
  );

  return (
    <>
      <QueueModal
        open={createModalOpen || Boolean(editingQueue)}
        submitting={submittingData}
        sipQueue={editingQueue}
        onClose={closeModal}
        onAccept={updateQueue}
      />
      <Grid
        container
        spacing={1}
        sx={{
          alignContent: 'center',
          marginBottom: 1,
        }}>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}>
          <TextField
            fullWidth
            variant='outlined'
            label='Search'
            id='search'
            name='search'
            defaultValue={search.search}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Grid>

        <Grid
          size={{
            md: 5,
          }}
          sx={{ display: { xs: 'none', sm: 'block' } }}></Grid>

        <Grid
          style={{ display: 'flex', alignItems: 'center' }}
          size={{
            xs: 12,
            md: 3,
          }}>
          <Button
            variant='contained'
            color='primary'
            disableElevation
            fullWidth
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}>
            Create Queue
          </Button>
        </Grid>
      </Grid>
      <AsyncLoader isLoading={loading && isListEmpty}>
        <Grid size={12}>
          <List>{displayList}</List>
          {loading && <DotLoader align='center' />}
          {!loading && !hasMore && !isListEmpty && (
            <Typography variant='body2' align='center' color='textSecondary'>
              No more results to display
            </Typography>
          )}
          {error && (
            <Typography variant='body2' align='center' color='textSecondary'>
              Failed to load SIP queues
            </Typography>
          )}

          {noResult && !debouncedSearch && (
            <EmptyState
              type='no-items-3'
              text='No SIP Queues currently exist'
              subText='Create a SIP Queue by clicking the button below'
              action={() => setCreateModalOpen(true)}
              actionText='Create SIP Queue'
            />
          )}
          {noResult && debouncedSearch && (
            <EmptyState
              type='no-records-found'
              text='No queues found matching your search criteria'
              subText='Try alternate words or selections.'
            />
          )}
        </Grid>
      </AsyncLoader>
    </>
  );
};

export default QueuesList;
